import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CmEdSectionView} from "../../interface/cm-ed-section-view.interface";
import {CmEdSize} from "../../interface/cm-ed-size.type";
import {CmEdFieldView} from "../../interface/cm-ed-field-view.interface";
import {CmEdGroupView} from "../../interface/cm-ed-group-view.interface";
import {CmContractExternalDataFoldPt} from "../../interface/cm-contract-external-data-fold-pt.interface";
import {CmEdEmphasis} from "../cm-ed-kv-list/cm-ed-kv-list.component";
import {CmEdHue, cmEdHueTokens, CmEdHueTokens, cmEdSparteHue} from "../cm-ed-hue.util";
import {cmEdCountHits, cmEdHasHit} from "../cm-ed-search-hit.util";

/**
 * ONE run of pairs inside a section — its Basisdaten, its Weitere Angaben, and what neither shows.
 *
 * A section carries at least one run (its own pairs) and one more per delivered row: a Beitrag with
 * two price slices is three runs. They are ONE shape and are rendered by one template branch, so the
 * section's own pairs and a price slice can never grow different expander behaviour — the drift that
 * left the premium block rendering ~36 pairs flat while every card beside it showed four.
 *
 * Built in `ngOnChanges` and stored, never derived in a getter: a getter would allocate three arrays
 * per run on every change-detection pass and `@for` would re-diff them while the reader merely moves
 * the mouse.
 */
export interface CmEdSectionRun {

    /** Stable anchor — the row's own key, or the section's key for its own pairs. Never a position. */
    key: string;

    /** The row's heading relative to its section. `null` for the section's own pairs. */
    title: string | null;

    /** Second line of the row. `null` renders none. */
    sub: string | null;

    /** `displayLevel` 1 — always on screen, in the order the host sorted them. */
    basis: CmEdFieldView[];

    /** `displayLevel` 2 — behind this run's own expander, under a "Weitere Angaben" run label. */
    weitere: CmEdFieldView[];

    /**
     * How many further fields this run does NOT show — `displayLevel` 3.
     *
     * Stated beside the expander instead of rendered, exactly as on the Deckung card. Every one of
     * them is on screen in "Alle Daten", which renders every block, every row and every level, so the
     * number is a signpost and never an apology for lost data.
     */
    restCount: number;

    /**
     * The delivered ROW this run was built from — `null` for the section's own pairs.
     *
     * Kept as a live REFERENCE rather than as copied flags: it is where the row's search roll-up lives
     * ({@link CmEdGroupView#hitCount}, which counts the row's matching fields at ALL levels plus its
     * own matching title, sub and cross-link labels), and the host may recompute that roll-up on the
     * very same object while the reader types. A number captured at build time would be a number that
     * silently stops being true mid-search.
     *
     * It is what lets a section report a row that matched on its TITLE — a hit no field of the run
     * carries and which counting fields alone would swallow, in direct breach of the rule that a
     * search must never silently under-report.
     */
    source: CmEdGroupView | null;

    /**
     * The `displayLevel` 3 fields this run was handed and does NOT render — the physically present
     * part of {@link restCount}.
     *
     * Retained rather than merely counted, for exactly one reason: {@link source}'s roll-up spans all
     * three levels, and the section may only ever claim the hits a reader can REACH here. Subtracting
     * the hits of these fields is what turns the row's total into the reachable one. They are the same
     * objects "Alle Daten" renders, so nothing is copied and nothing can drift.
     */
    rest: CmEdFieldView[];

    /** DOM id of the revealed block — the `aria-controls` target of this run's expander. */
    bodyId: string;

    /**
     * THE READER'S OWN state of this run's level-2 block. Component-local and never persisted.
     *
     * Closed at rest, EXCEPT for a run that has no Basisdaten to show above the control — see
     * {@link CmEdBlockSectionComponent#buildRun}.
     *
     * Deliberately NOT written by the search's automatic reveal: a run force-opened because the term
     * was found inside it must fall shut again exactly as the reader left it — see
     * {@link CmEdBlockSectionComponent#runOpen}.
     */
    expanded: boolean;

    /**
     * The reader's decision DURING an active search — `null` while they have not made one.
     *
     * READER INTENT WINS: a run the search opened can still be closed, and one the term left shut can
     * still be opened. Dropped whenever the term or the section changes, because a new term is a new
     * question. Never persisted.
     */
    searchOverride: boolean | null;

    /** The expander's `aria-expanded` / `aria-controls`; see {@link CmContractExternalDataFoldPt}. */
    foldPt: CmContractExternalDataFoldPt;

    /**
     * The open/closed value {@link foldPt} currently states.
     *
     * `foldPt` is cached on THIS rather than rebuilt in the toggle handler: `[pt]` is read by PrimeNG
     * when the binding's identity changes, so the object must be new exactly when the run's effective
     * open state changes — and that now moves for three separate reasons (the reader's click, the
     * search flipping on or off, and the host recomputing hits on the same field objects while the
     * term grows), of which a rebuild-on-toggle covered one.
     */
    foldOpen: boolean;
}

/**
 * ONE plain section of an overview — the renderer of every delivered family that has no card of its
 * own.
 *
 * <h2>The component that makes "nothing is dropped" true</h2>
 * The overviews render a card per person, per Deckung and per object. Everything else the server
 * delivers used to reach no overview at all: a Sparte's OWN fields (16 of them ranked `displayLevel`
 * 1 on the committed fixture), the whole Beitrag picture, and — the case that keeps recurring —
 * any family added to the payload after this frontend was built. The block list is assembled
 * server-side from config, so that last case is not hypothetical: a partner onboarding can introduce
 * a family without a frontend deploy.
 *
 * This component is the answer to all three. It renders a heading, an optional sub line, a list of
 * pairs and the section's rows, and it knows nothing about which family it was given — which is
 * exactly what lets it render a family that did not exist when it was written. A family may be
 * rendered PLAINLY; it may never be rendered NOWHERE.
 *
 * <h2>A section obeys the LEVEL model, like every card beside it</h2>
 * It used to render `displayLevel` 1 and 2 as one flat list, on the argument that "a section is not a
 * card". The Beitrag block is what disproved it: two price slices of ~18 fields each put 36 pairs on
 * screen before the reader reached a single Deckung, and pushed the Personen block below the fold —
 * while every card next to it showed four figures and hid the rest. That is the same flat-table
 * defect the whole screen was built to replace, and the level was invisible on top of it.
 *
 * Every run of pairs therefore renders as: Basisdaten always, then ONE expander revealing Weitere
 * Angaben under a labelled rule, then a count of what only "Alle Daten" carries. The expander is the
 * card's expander — same control, same wording, same non-persistence — so the sheet has ONE expander
 * idiom rather than one per component. See {@link CmEdSectionRun}.
 *
 * <h2>What it deliberately does NOT do</h2>
 * <ul>
 *   <li>It renders no pair itself: every one goes through `mvs-cm-ed-kv-list`, so the unit rule, the
 *       qualifier and the LEVEL LADDER have one definition on this sheet and this component adds no
 *       second one.</li>
 *   <li>It does not FORMAT, sort or filter by value. It renders the section it is handed, in order;
 *       the only split it performs is by the server's own `displayLevel`.</li>
 *   <li>It does not LOAD and holds no service. Its one piece of state is which expanders are open.</li>
 *   <li>It does not PERSIST that state. A reader returning to the sheet finds every run in its
 *       resting state, never one that silently hides half its data.</li>
 *   <li>It does not render a per-block FAILURE. That notice is `mvs-cm-ed-block-error`, placed by the
 *       view above its content, and an errored family is never given a section — see
 *       `cm-ed-view-sections.util.ts` for why a "Keine Angaben" under a failure would be a lie.</li>
 * </ul>
 */
@Component({
    selector: 'mvs-cm-ed-block-section',
    templateUrl: './cm-ed-block-section.component.html',
    styleUrls: ['./cm-ed-block-section.component.scss'],
    standalone: false,
})
export class CmEdBlockSectionComponent implements OnChanges {

    /** Counts every instance ever created, so two sections never share a DOM id. */
    protected static instanceCounter: number = 0;

    /** The section to render, built once per delivery by `cm-ed-view-sections.util.ts`. */
    @Input() section: CmEdSectionView;

    /** Width bucket, measured once by the host. Decides the type scale and hides the sub line at `xs`. */
    @Input() size: CmEdSize = 'lg';

    /**
     * How this section's pairs are laid out across its width — handed straight to `mvs-cm-ed-kv-list`.
     *
     * Decided by the CALL SITE and not here, because only the caller knows how wide it made this
     * section: the very same component is placed full width (the Beitrag under the Vertrag basics) and
     * inside a `col-4` of a card grid (a Sparte's own fields, "Weitere Daten"). `grid` in a card column
     * would put four pair columns into a third of the row; `row` at full width leaves the 1700 px
     * gutter this input exists to close. A section cannot measure itself — the sheet measures ONCE, in
     * the host — so the placement decision travels down with the placement.
     *
     * Defaults to `row`, i.e. to the narrow case: a call site that forgets it gets the layout that is
     * merely spacious, never the one that is unreadable.
     */
    @Input() layout: 'row' | 'grid' = 'row';

    /**
     * Whether a search term is currently active — passed through to the pairs, and the switch for the
     * two things the section itself does about a search.
     *
     * <h2>The section NEVER hides, NEVER dims and never drops a run label</h2>
     * A section with no hit renders exactly as it does at rest: same heading, same spine, same
     * figures, full opacity, and every "WEITERE ANGABEN" rule still in place. A search on this sheet
     * can only ADD a highlight; the dimming design was rejected outright by the user.
     *
     * <h2>Which leaves two jobs, both of which the honesty rule requires</h2>
     * <ul>
     *   <li>The section STATES its reachable hits ({@link hitCount}). With nothing dimmed, a Beitrag
     *       block holding two matches looks precisely like one holding none.</li>
     *   <li>A RUN whose Weitere Angaben hold a match opens itself — see {@link runOpen}. A match
     *       behind a closed expander is a match the reader is never shown.</li>
     * </ul>
     *
     * A boolean and not the term: this component has no use for the text, and being handed it is what
     * would tempt the next author into matching locally instead of reading the host's flags.
     */
    @Input() searching: boolean = false;

    /**
     * WHICH SECTION this is, as a hue — the second of the three jobs colour does on this sheet.
     *
     * The views choose it, because only they know what they are placing:
     * <pre>
     *   Vertrag   primary      Personen  blue
     *   Beitrag   green        Objekte   teal
     *   Sparte    its own accent — taken from the section, see {@link hueTokens}
     * </pre>
     * so a reader finds the premium block by colour before reading a heading, and a Sparte section
     * stays visibly tied to the Deckung cards built from it.
     *
     * It drives the heading colour and the accent spine TOGETHER, from one map
     * (`cm-ed-hue.util.ts`) — a heading and a spine of different families read as two sections.
     *
     * Defaults to `primary`, the theme's own accent: a call site that forgets it gets the colour the
     * section headings have always had.
     */
    @Input() hue: CmEdHue = 'primary';

    /**
     * The level of the section's OWN Basisdaten run — passed straight to `mvs-cm-ed-kv-list`.
     *
     * A pass-through, so a view can render a whole section one step quieter than the cards beside it
     * (a "Weitere Daten" column) without this component deciding that for it. The REVEALED run is not
     * affected: what an expander opens is Weitere Angaben by definition, and it always renders as
     * `weitere`.
     */
    @Input() emphasis: CmEdEmphasis = 'basis';

    /**
     * An optional lead-in above the section's own Basisdaten — e.g. "BASISDATEN".
     *
     * A pass-through to `mvs-cm-ed-kv-list`, offered so a view that stacks several runs of different
     * levels can name the first one. `null` renders nothing, which is right for the ordinary section:
     * the heading above it already says what follows, and a label under every heading would be the
     * noise that stops the device from working where it matters.
     */
    @Input() runLabel: string | null = null;

    /**
     * The section's runs: its own pairs first, then one per delivered row.
     *
     * Rebuilt whenever the section changes and stored, never derived — see {@link CmEdSectionRun}.
     */
    protected runs: CmEdSectionRun[] = [];

    /**
     * The hue this section actually wears, as its three utility classes.
     *
     * A Sparte OVERRIDES the input: `accentIndex` is the index its Deckung cards wear, and the two
     * must match or a section and the cards belonging to it stop reading as one family. Everything
     * else follows the view's choice.
     *
     * Reads a frozen module-scoped map, so this getter allocates nothing during change detection.
     */
    get hueTokens(): CmEdHueTokens {
        return cmEdHueTokens(cmEdSparteHue(this.section?.accentIndex) ?? this.hue);
    }

    /**
     * The accent SPINE of the section.
     *
     * Rendered for every section now, where it used to be suppressed whenever `accentIndex` was null.
     * The reason it was suppressed no longer holds: the bar used to say "this is Sparte number n" and
     * a section belonging to no Sparte had nothing to say, whereas it now says WHICH SECTION — which
     * the view states explicitly through {@link hue} and which is true of every section, the
     * unrecognised family included (it takes the default `primary`, i.e. the colour its heading has
     * always had). One rule, no branch, and the header band of a section reads like the header band of
     * a card.
     */
    get spineClass(): string {
        return this.hueTokens.spine;
    }

    /**
     * Text colour of the section HEADING — the same hue as the spine beside it.
     *
     * The heading has always been `text-primary`; it is now `text-primary` only for a primary
     * section, and follows its own hue otherwise. This is the one place a heading takes a colour: card
     * titles stay `text-color`, because a grid of a dozen coloured titles is a highlighter rather than
     * a hierarchy.
     */
    get headingClass(): string {
        return this.hueTokens.text;
    }

    /**
     * A section sized to its bucket: tighter padding where it sits in a narrow panel.
     *
     * Every step written out, the widest included. A `default` here would answer a step nobody
     * revisited with whatever the old widest step happened to be, which is the drift this ladder was
     * re-cut to end.
     */
    get padClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'p-3';
            case 'xs':
                // Below 560 px the section spans a panel that narrow; 1rem on each side is width the
                // values need more than the frame does.
                return 'p-2';
        }
    }

    /**
     * Type scale of the section heading — the same steps the cards use, so the two read as peers.
     *
     * `text-base` from `sm` up, i.e. from 560 px. It used to read `xs || sm`, which meant "below
     * 600 px" under the old ladder; `sm` now reaches to 759 px, and a section is FULL WIDTH there, so
     * carrying the small step up to 759 px would have shrunk the heading of the widest single-column
     * section on the whole ladder.
     */
    get titleClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'text-base';
            case 'xs':
                return 'text-sm';
        }
    }

    /**
     * True when the section carries nothing at all to show.
     *
     * Such a section is still RENDERED, with a sentence saying so. For a family the frontend does not
     * recognise the heading is the news: a reader who sees it and reads "Keine Angaben in dieser
     * Lieferung" has learned that the family arrived and was empty, which is a different fact from
     * never hearing of it — and an empty box with no explanation reads like a section that failed to
     * load, on a screen whose whole purpose is to prove nothing went missing.
     */
    get empty(): boolean {
        return !this.section?.fields?.length && !this.section?.groups?.length;
    }

    /**
     * How many matches this section holds that a reader can actually REACH here.
     *
     * The section's own HEADING, plus its own runs, plus every delivered row — summed from
     * {@link titleHits} and {@link runHits}, so the marker in the heading and the highlights in the
     * body are two readings of one number.
     *
     * The heading term was missing until now, and its absence was the visible half of the same defect
     * {@link titleHits} closes: a reader searching a Sparte name watched the sheet header's total rise
     * while this section — the one holding the match — declared nothing and marked nothing.
     */
    get hitCount(): number {

        if (!this.searching) {
            return 0;
        }

        let hits = this.titleHits;

        for (const run of this.runs) {
            hits += this.runHits(run);
        }

        return hits;
    }

    /**
     * HOW MANY OF THE SECTION'S OWN HEADING TEXTS the term matched — its {@link CmEdSectionView#label}
     * and its {@link CmEdSectionView#sub}, at most one each.
     *
     * <h2>The gap this closes</h2>
     * The host counts a section's label and sub into the sheet's total (`countTextHits(label, sub)` on
     * the Sparte, the Beitrag and every unrecognised block). This component could not see it: the
     * section view model was the ONE shape reaching a renderer with no roll-up at all, and this very
     * file used to say so in its own TSDoc. The consequence was a count asserting something the screen
     * did not show — search a Sparte name, watch the number rise, find nothing marked — which is the
     * one thing this feature may not do. {@link CmEdSectionView#hits} is the pointer that closes it.
     *
     * <h2>By subtraction, like a row heading, because the host only ever publishes totals</h2>
     * `hits.hitCount` is a TOTAL over the family this section was built from. Everything in it that
     * the BODY accounts for is subtracted, and what is left is the heading's share — the same device
     * {@link runTitleHit} already applies to a row heading, deliberately, so a Sparte name and a row
     * name are never marked two different ways on one screen.
     *
     * <h2>The one asymmetry in the totals, and how it is handled</h2>
     * `CmEdBlockView.hitCount` folds its rows in; `CmEdBeitragView.hitCount` deliberately does NOT
     * (the host adds the price rows separately, so folding them in would count one figure twice). Both
     * arrive here through the same narrowed {@link CmEdSearchRollUp}, which by design says nothing
     * about which family it came from. The row roll-ups are therefore subtracted only when the total
     * is large enough to have contained them — and never otherwise, so this can under-state a heading
     * but can never mark one the term did not touch. A marked heading is a claim; an unmarked one is
     * merely a miss, and of the two only the claim may be wrong.
     *
     * Clamped to the number of heading texts that actually exist, because that is the most the host
     * can have counted here and a section with no sub line has only one.
     *
     * RESIDUAL, stated rather than hidden: a Beitrag delivered as TWO OR MORE price slices whose rows
     * also match will under-state — the subtraction removes row hits the Beitrag total never carried.
     * It needs a multi-slice premium AND a row match AND at least as many row hits as heading hits.
     * The fix is not here: either `CmEdBeitragView.hitCount` folds its rows in like every other block
     * (with `recountActiveView` dropping its separate addition), or the roll-up gains a heading-only
     * count. Both are host changes.
     */
    get titleHits(): number {

        const rollUp = this.section?.hits;

        if (!this.searching || !rollUp) {
            return 0;
        }

        let own = 0;
        let rows = 0;

        for (const run of this.runs) {

            if (run.source) {
                // The row's FULL roll-up, not its reachable part: a block total folds in the row's
                // level-3 fields too, and subtracting less than was added would leak into the heading.
                rows += run.source.hitCount;
            } else {
                own += this.runFieldHits(run) + cmEdCountHits(run.rest);
            }
        }

        const body = rollUp.hitCount >= own + rows ? own + rows : own;
        const texts = (this.section.label ? 1 : 0) + (this.section.sub ? 1 : 0);

        return Math.min(texts, Math.max(0, rollUp.hitCount - body));
    }

    /**
     * True when the term matched the section's own heading — the switch for the heading's mark.
     *
     * The heading is marked as a WHOLE and never run by run: the host splits labels and values into
     * highlight runs and splits no heading, so which characters matched is not knowledge this
     * component has. Marking the whole heading states exactly what is known and no more.
     */
    get titleHit(): boolean {
        return this.titleHits > 0;
    }

    /**
     * The hits of ONE run that are reachable in THIS section.
     *
     * <h2>Reachable, and nothing but reachable</h2>
     * Basisdaten and Weitere Angaben are on screen — the latter because a run holding a match opens
     * itself (see {@link runOpen}), so it is reachable by definition. The `displayLevel` 3 fields
     * behind "{n} nicht angezeigt" are NOT: no expander setting brings them onto the screen, and
     * counting them here would promise the reader hits this section cannot show. They are not
     * swallowed either — the sheet's own header states them as "… weitere nicht angezeigt", which is
     * where a count spanning the whole delivery belongs. A match must be claimed exactly once, never twice and
     * never nowhere.
     *
     * <h2>Why a row is not counted by adding up its fields</h2>
     * A row matches on its TITLE, its sub and its cross-link labels as well as on its fields — someone
     * searching "Anna Meier" is looking for the rows she is named on. None of those is a field, so
     * counting fields alone would report 0 for a row that plainly matched, which is precisely the
     * silent under-reporting the honesty rule forbids. {@link CmEdGroupView#hitCount} is the host's
     * roll-up over all of it, so the reachable total is that roll-up MINUS the level-3 field hits this
     * view drops — exact in every combination, including a row that matched on its title AND on two of
     * its figures.
     *
     * The section's OWN run has no roll-up of its own — its fields are counted directly. That used to
     * mean a term matching the section's LABEL or sub was invisible here; it no longer does. The
     * SECTION's roll-up now arrives as {@link CmEdSectionView#hits} and its heading share is read by
     * {@link titleHits}, which {@link hitCount} adds on top of this sum.
     *
     * Clamped at zero: the two numbers come from the same host pass, but a roll-up that ever lagged
     * the fields by a frame must not render "-1 Treffer".
     */
    runHits(run: CmEdSectionRun): number {

        if (!this.searching || !run) {
            return 0;
        }

        if (run.source) {
            return Math.max(0, run.source.hitCount - cmEdCountHits(run.rest));
        }

        return this.runFieldHits(run);
    }

    /** The hits carried by the run's own rendered PAIRS — Basisdaten and Weitere Angaben. */
    runFieldHits(run: CmEdSectionRun): number {
        return cmEdCountHits(run?.basis) + cmEdCountHits(run?.weitere);
    }

    /**
     * True when this run accounts for more reachable hits than its rendered pairs do — i.e. the
     * surplus is the ROW'S OWN text: its title, its sub, or one of its cross-link labels.
     *
     * That surplus is what marks the row heading. Without it a section could count a hit the reader
     * can see no reason for anywhere in the body — a row named "Anna Meier" matched by exactly that
     * term, with every one of its figures untouched. The row's own texts carry no highlight runs (the
     * host splits labels and values, not headings), so the heading is marked as a whole instead.
     *
     * Exact by subtraction, so a row that matched on its title AND on two figures still marks its
     * heading, while a row whose only match is a level-3 field marks nothing — that hit is not
     * reachable here and is reported by the sheet header instead.
     */
    runTitleHit(run: CmEdSectionRun): boolean {
        return this.searching && this.runHits(run) > this.runFieldHits(run);
    }

    /** True while a term is active AND this run's Weitere Angaben hold one of its matches. */
    runHit(run: CmEdSectionRun): boolean {
        return this.searching && cmEdHasHit(run?.weitere);
    }

    /**
     * WHETHER ONE RUN'S level-2 block is on screen — the reader's own state, temporarily overruled by
     * a search that found something behind that run's expander.
     *
     * <pre>
     *   no term             the reader's own `expanded`, untouched
     *   term, no override   open if the reader had it open OR the term matched inside Weitere Angaben
     *   term, override      the reader's decision, which always wins
     * </pre>
     *
     * The reveal is an OVERLAY and never a write: `expanded` is left alone, so clearing the term
     * returns every run to precisely the state its reader left it in. Same contract as the three
     * cards, because the sheet has ONE expander idiom and an auto-opening card beside a stubbornly
     * shut section would have to be learned twice.
     */
    runOpen(run: CmEdSectionRun): boolean {

        if (!run) {
            return false;
        }

        if (!this.searching) {
            return run.expanded;
        }

        return run.searchOverride ?? (run.expanded || this.runHit(run));
    }

    /**
     * The expander's `aria-expanded` / `aria-controls` for ONE run, cached on the value it states.
     *
     * Returns the SAME object while the run's effective open state is unchanged and a new one exactly
     * when it changes, which is the contract `[pt]` is read under. See {@link CmEdSectionRun#foldOpen}
     * for why a rebuild in `toggle()` is no longer sufficient.
     */
    foldPtOf(run: CmEdSectionRun): CmContractExternalDataFoldPt {

        const open = this.runOpen(run);

        if (run.foldOpen !== open) {
            run.foldOpen = open;
            run.foldPt = CmEdBlockSectionComponent.buildFoldPt(run.bodyId, open);
        }

        return run.foldPt;
    }

    /**
     * Rebuilds the runs whenever the section changes, and drops every search-time override when the
     * search state changes.
     *
     * Guarded on the inputs: a size or hue change re-renders the same runs and re-splitting the same
     * arrays for it would be work with no result.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes["section"]) {
            this.buildRuns();
        }

        if (changes["searching"]) {
            for (const run of this.runs) {
                run.searchOverride = null;
            }
        }
    }

    /**
     * Opens / closes ONE run's level-2 block.
     *
     * While a term is active the click is recorded as an OVERRIDE rather than as the reader's resting
     * state: a run the search opened must be closable, and a run closed while searching must not still
     * be closed once the term is cleared. Without the split, `expanded = !expanded` on a force-opened
     * run would flip a flag that was not what put the block on screen, and the control would appear to
     * do nothing.
     *
     * `foldPt` is no longer rebuilt here — {@link foldPtOf} refreshes it from the effective open state,
     * which also covers the two causes a toggle handler cannot see.
     */
    toggle(run: CmEdSectionRun): void {

        if (this.searching) {
            run.searchOverride = !this.runOpen(run);
            return;
        }

        run.expanded = !run.expanded;
    }

    /**
     * The section's own pairs, then one run per delivered row.
     *
     * The own run is omitted when the section has neither own fields NOR anything left over — a
     * Beitrag delivered as two price slices carries its figures on the ROWS — so the section never
     * opens with an empty run and a stray separator. A section with no own fields but a level-3 count
     * DOES get one: that run renders as the count hint alone, which is the sentence a reader needs in
     * order not to conclude the fields were never delivered.
     *
     * <h2>The reader's open runs SURVIVE a rebuild of the same run</h2>
     * A rebuild used to reset every expander to closed. That was harmless while the section input only
     * changed on a new delivery, and became a defect the moment a search arrived: the host rebuilds the
     * view models as the term grows, so every keystroke would have slammed shut every run the reader
     * had opened — while the level-2 pairs they were reading blinked out from under them.
     *
     * A run's {@link CmEdSectionRun#key} is the source row's own stable anchor, so a run that comes
     * back with the same key IS the same run and keeps the reader's `expanded`. A run that is genuinely
     * new — a family that was not in the previous delivery — still starts from its computed default,
     * which is what "not persisted" was ever meant to protect.
     */
    protected buildRuns(): void {

        const instance = ++CmEdBlockSectionComponent.instanceCounter;
        const runs: CmEdSectionRun[] = [];
        const ownFields = this.section?.fields ?? [];
        const ownRest = this.section?.restCount ?? 0;

        // The runs as they stand, by their stable anchor — read once so the carry-over below is a
        // lookup rather than a scan per run.
        const previous = new Map<string, CmEdSectionRun>(this.runs.map(run => [run.key, run]));

        if (ownFields.length || ownRest) {
            const key = this.section.key + '#own';
            runs.push(CmEdBlockSectionComponent.buildRun(
                'cmEdSection' + instance + '-own',
                key,
                null,
                null,
                ownFields,
                // The block's own level-3 count, which the host already computed for the families
                // whose payload carries levels 1 and 2 only. It is ADDED to whatever level-3 fields
                // are physically present in `fields` (an unrecognised family is handed every level),
                // because both mean exactly one thing to the reader: n such fields exist, they are
                // not on this screen, and "Alle Daten" has them.
                ownRest,
                null,
                previous.get(key)));
        }

        for (const group of (this.section?.groups ?? [])) {
            runs.push(CmEdBlockSectionComponent.buildRun(
                'cmEdSection' + instance + '-' + runs.length,
                group.key,
                group.title,
                group.sub,
                group.fields ?? [],
                0,
                group,
                previous.get(group.key)));
        }

        this.runs = runs;
    }

    /**
     * ONE run, split by the field's level and by nothing else.
     *
     * <h2>The NORMALISED level, not the raw `displayLevel`</h2>
     * `CmEdFieldView.level` is `1 | 2 | 3` by construction — the host folded an absent or
     * out-of-range server rank down to 3 and counted `CmEdCounts.basis/weitere/detail` from that same
     * value. Reading `dto.displayLevel` here instead sent such a field to `weitere`, i.e. onto the
     * screen under a "Weitere Angaben" rule, while the ÜBERBLICK strip above it counted the very same
     * field as Detaildaten. One field, two levels, stated in two places on one page. Reading the
     * lifted member is what makes the split and the counts agree by construction.
     *
     * A field of an unknown level therefore reaches this method as level 3 and is COUNTED rather than
     * dropped: the split is a PRESENTATION decision and must never cost a delivered field, and every
     * counted field is on screen in "Alle Daten".
     *
     * <h2>A run with no Basisdaten opens by itself</h2>
     * A family that delivered level-2 fields and nothing above them would otherwise render as a
     * heading with a lone "{n} weitere Angaben →" under it and no data at all — which reads like a
     * section that failed to load, on the one screen built to prove nothing went missing. Such a run
     * therefore starts OPEN: the pairs are on screen, they still say "Weitere Angaben" through the run
     * label and the emphasis step, and the expander still closes them. The expander exists to keep a
     * long tail from burying the basics; where there are no basics there is nothing to bury.
     */
    protected static buildRun(bodyId: string,
                              key: string,
                              title: string | null,
                              sub: string | null,
                              fields: CmEdFieldView[],
                              restCount: number,
                              source: CmEdGroupView | null,
                              previous?: CmEdSectionRun): CmEdSectionRun {

        const basis: CmEdFieldView[] = [];
        const weitere: CmEdFieldView[] = [];

        // The level-3 fields are KEPT, not merely counted: the run's reachable hit total is the row's
        // own roll-up minus the hits sitting in exactly these — see runHits.
        const rest: CmEdFieldView[] = [];

        for (const field of fields) {

            const level = field?.level;

            if (level === 1) {
                basis.push(field);
            } else if (level === 2) {
                weitere.push(field);
            } else {
                rest.push(field);
            }
        }

        // The reader's own state wins over the computed default whenever this run was already on
        // screen under the same key — see buildRuns for why a rebuild may no longer reset it.
        const expanded = previous ? previous.expanded : (!basis.length && weitere.length > 0);

        return {
            key: key,
            title: title,
            sub: sub,
            basis: basis,
            weitere: weitere,
            restCount: restCount + rest.length,
            source: source,
            rest: rest,
            bodyId: bodyId,
            expanded: expanded,
            // A rebuild is a new term or a new delivery, i.e. a new question: the answer the reader
            // gave to the old one does not carry over.
            searchOverride: null,
            foldOpen: expanded,
            foldPt: CmEdBlockSectionComponent.buildFoldPt(bodyId, expanded),
        };
    }

    /**
     * Rebuilt on every toggle rather than mutated: `[pt]` is read by PrimeNG when the binding's
     * identity changes, so mutating the object in place would leave `aria-expanded` at its first value
     * and tell a screen reader the opposite of what is on screen.
     */
    protected static buildFoldPt(bodyId: string, expanded: boolean): CmContractExternalDataFoldPt {
        return {
            root: {
                'aria-expanded': String(expanded),
                'aria-controls': bodyId,
            },
        };
    }
}
